import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selection", "expirationFields", "expirationMode", "expirationDate"]
  static values = { today: String }

  connect() {
    this.syncState()
  }

  onChange(event) {
    const input = event.target
    if (!(input instanceof HTMLInputElement)) return

    if (input.name?.endsWith("[selection]")) {
      this.syncState()
      return
    }

    if (input.name?.endsWith("[complimentary_expiration_mode]")) {
      this.applyExpirationMode(input)
      return
    }

    if (input.name?.endsWith("[complimentary_expiration_date]") && input.value) {
      this.syncPresetRadiosToDate(input.value)
    }
  }

  openDatePicker() {
    this.selectExpirationMode("on_date")
    if (typeof this.expirationDateTarget.showPicker !== "function") return

    try {
      this.expirationDateTarget.showPicker()
    } catch (_error) {
      return
    }
  }

  syncState() {
    const selection = this.selectionTargets.find((input) => input.checked)?.value
    const supportsExpiration = ["basic", "full", "premium"].includes(selection)

    if (this.hasExpirationFieldsTarget) {
      this.expirationFieldsTarget.setAttribute("aria-hidden", supportsExpiration ? "false" : "true")
    }

    if (!supportsExpiration) {
      this.selectExpirationMode("never")
      if (this.hasExpirationDateTarget) this.expirationDateTarget.value = ""
    } else if (!this.expirationModeTargets.some((input) => input.checked)) {
      if (!this.expirationDateTarget.value) this.selectExpirationMode("never")
    }

    this.setExpirationControlsEnabled(supportsExpiration)
  }

  setExpirationControlsEnabled(enabled) {
    this.expirationModeTargets.forEach((input) => {
      input.disabled = !enabled
    })

    if (this.hasExpirationDateTarget) {
      this.expirationDateTarget.disabled = !enabled
    }
  }

  applyExpirationMode(selected) {
    if (selected.value === "never") {
      this.expirationDateTarget.value = ""
      return
    }

    const months = this.monthsFor(selected)
    if (months) {
      this.expirationDateTarget.value = this.isoDateFromMonths(months)
      return
    }

    if (selected.value === "on_date") this.openDatePicker()
  }

  selectExpirationMode(value) {
    this.expirationModeTargets.forEach((input) => {
      input.checked = input.value === value
    })
  }

  syncPresetRadiosToDate(value) {
    const matchingPreset = this.expirationModeTargets.find((input) => {
      const months = this.monthsFor(input)
      return months && this.isoDateFromMonths(months) === value
    })

    this.expirationModeTargets.forEach((input) => {
      if (matchingPreset) {
        input.checked = input === matchingPreset
        return
      }

      input.checked = input.value === "on_date"
    })
  }

  monthsFor(input) {
    const fromValue = String(input.value || "").match(/^(\d+)_months?$/)
    if (fromValue) return Number.parseInt(fromValue[1], 10)

    const months = Number.parseInt(input.dataset.months || "", 10)
    return Number.isInteger(months) && months > 0 ? months : null
  }

  isoDateFromMonths(months) {
    const [year, month, day] = this.todayValue.split("-").map(Number)
    const targetMonthIndex = month - 1 + months
    const targetYear = year + Math.floor(targetMonthIndex / 12)
    const targetMonth = targetMonthIndex % 12
    const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate()
    const targetDay = Math.min(day, lastDay)

    return [
      targetYear,
      String(targetMonth + 1).padStart(2, "0"),
      String(targetDay).padStart(2, "0")
    ].join("-")
  }
}
